import React, { Component } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import _ from "lodash";

import { DailyActivityService } from "../../../../Common/Services/DailyActivity";
import { ReactPageLoader } from "../../../../Common/Components/ReactPageLoader";
import {
  IStatisticsEntryRequest,
  IStatisticsPostRequest,
} from "../../../../Common/Contracts/IStatisticsEntryRequest";
import { Utils } from "../../../../Common/Utilis";
import InputeEllipisWithTooltip from "../../../../Common/Components/InputeEllipisWithTooltip";

class Statistics extends Component<any, any> {
  private scrollPos: any;
  constructor(props: any) {
    super(props);
    this.scrollPos = React.createRef();
    this.state = {
      searchInput: "",
      hideEmptyRows: false,
      data: [],
      unsavedChanges: false,
      isDataLoading: false,
      isModified: false,
      isDataSaving: false,
    };
  }

  componentDidMount(): void {
    this.getStatisticsData();
  }

  componentDidUpdate(prevProps) {
    if (
      !this.props.isLoadingDefaultDate &&
      (prevProps.selectedDate !== this.props.selectedDate ||
        prevProps.filterHotelId !== this.props.filterHotelId)
    ) {
      if (this.props.selectedDate) {
        this.getStatisticsData();
      } else {
        this.setState({ data: [] });
      }
    }
  }

  getStatisticsData = async () => {
    const { filterHotelId, selectedDate } = this.props;
    if (!filterHotelId || !selectedDate) {
      return;
    }

    this.setState({ searchTerm: "", isDataLoading: true });

    try {
      const statisticsData =
        (await DailyActivityService.GetStatisticsData(
          filterHotelId,
          selectedDate
        )) ?? [];

      // Find total values for ROOMS OCCUPIED and COMP ROOMS
      const roomsOccupiedTotal =
        statisticsData.find(
          (item) => item.itemName.toLowerCase() === "rooms occupied"
        )?.total || 0;
      const compRoomsTotal =
        statisticsData.find(
          (item) => item.itemName.toLowerCase() === "comp rooms"
        )?.total || 0;
      const prevRoomsOccupied =
        statisticsData.find(
          (item) => item.itemName.toLowerCase() === "rooms occupied"
        )?.prevTotal || 0;
      const arrivals =
        statisticsData.find(
          (item) => item.itemName.toLowerCase() === "arrivals"
        )?.total || 0;
      const departures =
        parseInt(prevRoomsOccupied) +
        parseInt(arrivals) -
        parseInt(roomsOccupiedTotal);
      const updatedData = statisticsData.map((item) => {
        if (item.autoCalculate === 6) {
          return {
            ...item,
            total: roomsOccupiedTotal - compRoomsTotal, // Subtract total values
          };
        }
        if (item.autoCalculate === 4) {
          return {
            ...item,
            total: departures,
          };
        }
        if (item.autoCalculate === 2) {
          return {
            ...item,
            total: parseInt(prevRoomsOccupied) - departures,
          };
        }
        return item;
      });

      this.setState({ data: [] }, () => {
        this.setState({
          data: updatedData,
          isDataLoading: false,
        });
      });
    } catch (error) {
      this.setState({ data: [], isDataLoading: false });
    } finally {
      if (this.state.isModified) {
        this.handleDiscard();
      }
    }
  };

  borderFormatter = (column, colIndex) => {
    return (
      <>
        {column.text}
        <div className="border-bottom"></div>
      </>
    );
  };

  getColumns = () => {
    const baseColumns: any[] = [
      {
        dataField: "itemName",
        text: "Name",
        headerClasses: "big-code-width",
        headerFormatter: this.borderFormatter,
        formatter: (cell, row, rowIndex) =>
          this.renderNameWithDescriptionTooltip(
            cell,
            row,
            rowIndex,
            "itemName"
          ),
      },
      {
        dataField: "",
        text: "Total",
        headerClasses: "align-right width-116",
        headerFormatter: this.borderFormatter,
        classes: "align-right",
        formatter: (cell, row, rowIndex) => (
          <Form.Control
            className="width-100 text-right"
            type="text"
            disabled={row.isDisabled}
            onKeyDown={Utils.AllowOnlyPositiveNumnberInputWithoutDot}
            value={row?.total}
            onChange={(e) => this.handleInputChange(e, row.itemUniqueNo, "total")}
            onBlur={(e) => this.handleBlur(e, row, rowIndex)}
          />
        ),
      },
      {
        dataField: "",
        text: "Explanation",
        headerClasses: "notes-width",
        headerFormatter: this.borderFormatter,
        formatter: (cell, row, rowIndex) =>
          this.renderExplanation(cell, row, rowIndex, "explanation"),
      },
      {
        dataField: "",
        text: "",
        headerClasses: "info-width",
        headerFormatter: this.borderFormatter,
        formatter: (cell, row, rowIndex) =>
          this.renderFormulaForCalculation(cell, row, rowIndex),
      },
    ];

    return baseColumns;
  };

  renderNameWithDescriptionTooltip = (
    cell: any,
    row: any,
    rowIndex: number,
    field: string
  ) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <span className="label">{row?.[field] || ""}</span>
        {row.notes && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-description-${rowIndex}`}>
                {row.notes}
              </Tooltip>
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ cursor: "pointer" }}
            >
              <path
                d="M8.25 5.25H9.75V6.75H8.25V5.25ZM8.25 8.25H9.75V12.75H8.25V8.25ZM9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.6925 15 3 12.3075 3 9C3 5.6925 5.6925 3 9 3C12.3075 3 15 5.6925 15 9C15 12.3075 12.3075 15 9 15Z"
                fill="#6A6E73"
              />
            </svg>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  renderExplanation = (
    cell: any,
    row: any,
    rowIndex: number,
    field: string
  ) => {
    return (
      <div>
        {row.explanationReq === "True" && (
          <InputeEllipisWithTooltip
            placement="bottom"
            inputeVal={row?.explanation || ""}
          >
            <Form.Control
              maxLength={900}
              value={row?.[field] || ""}
              className={
                row?.showExplanationRequired ? "notes red-border" : "notes"
              }
              onChange={(e) => this.handleInputChange(e, row.itemUniqueNo, field)}
            />
          </InputeEllipisWithTooltip>
        )}
      </div>
    );
  };

  renderFormulaForCalculation = (cell, row, rowIndex) => {
    const autoCalc = row?.autoCalculate;
    const itemName = row?.itemName || "";
    let tooltipMessage = "";

    // Only build tooltip message when isDisabled === 1
    if (row?.isDisabled === 1) {
      debugger
      if (autoCalc === 2) {
        tooltipMessage = `${itemName} = (Rooms Occupied from current day - Arrival Rooms from current day)`;
      } else if (autoCalc === 4) {
        tooltipMessage = `${itemName} = (Rooms Occupied from previous day - Stay Overs from current day)`;
      } else {
        tooltipMessage = `${itemName} = (Rooms Occupied - Complimentary Rooms)`;
      }
    }

    return (
      <>
        {tooltipMessage && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-description-${rowIndex}`}>
                {tooltipMessage}
              </Tooltip>
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"              
            >
              <path
                d="M8.25 5.25H9.75V6.75H8.25V5.25ZM8.25 8.25H9.75V12.75H8.25V8.25ZM9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.6925 15 3 12.3075 3 9C3 5.6925 5.6925 3 9 3C12.3075 3 15 5.6925 15 9C15 12.3075 12.3075 15 9 15Z"
                fill="#6A6E73"
              />
            </svg>
          </OverlayTrigger>
        )}
      </>
    );
  };

  getFilteredData = () => {
    const { searchInput, hideEmptyRows, data } = this.state;
    const lowerSearchTerm = searchInput.toLowerCase();
    let filteredData = _.cloneDeep(data);

    filteredData = hideEmptyRows
      ? filteredData.filter(
        (row: any) =>
          row?.total !== "" &&
          row?.total != null &&
          Number(row?.total) !== 0
      )
      : filteredData;

    filteredData = filteredData.filter((row: any) => {
      return (
        row.itemName?.toLowerCase().includes(lowerSearchTerm) ||
        row.total?.toString().toLowerCase().includes(lowerSearchTerm) ||
        row.explanation?.toLowerCase().includes(lowerSearchTerm)
      );
    });

    return filteredData;
  };

  validateAndSave = async () => {
    const { filterHotelId, selectedDate } = this.props;
    let { data }: any = this.state;

    const modifiedRows = data.filter((row) => row.isModified);

    if (modifiedRows.length === 0) {
      toast.info("No changes to save.");
      return;
    }

    // Check if "Rooms Occupied" is modified
    const isRoomOccupiedModified = modifiedRows.some(
      (row) => row?.itemName?.toLowerCase() === "rooms occupied"
    );

    if (isRoomOccupiedModified) {
      const response: any = await DailyActivityService.isExitsLabourHouseKepping(
        selectedDate,
        filterHotelId
      );

      if (response.success == "Success") {
        confirmAlert({
          title: "Confirm?",
          message:
            "Making this change will un-approve the following housekeeping days: " +
            response.message +
            ". Are you sure you want to continue to Save the data?",
          buttons: [
            {
              label: "Yes",
              onClick: () => this.checkAccountingPeriod(),
            },
            { label: "No", onClick: () => this.handleDiscard() },
          ],
        });
      } else {
        this.checkAccountingPeriod();
      }
    } else {
      this.checkAccountingPeriod();
    }
  };

  checkAccountingPeriod = async () => {
    const { filterHotelId, selectedDate } = this.props;
    if (!filterHotelId || !selectedDate) {
      return;
    }
    try {
      const response: any = await DailyActivityService.CheckAccountingPeriod(
        selectedDate,
        filterHotelId,
        'Accounting',
        true
      );
      if (response.success) {
        this.handleSave();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  }

  handleSave = async () => {
    const { filterHotelId, selectedDate } = this.props;
    let { data }: any = this.state;

    this.setState({ isDataSaving: true });
    const entries: IStatisticsEntryRequest[] = data.map((entry) => ({
      Hid: filterHotelId,
      Date: selectedDate,
      ItemNo: entry.itemNo,
      ItemName: entry.itemName,
      Total: entry.total,
      RoomRevenueTotal: 0,
      BrandCode: entry.brandCode || 0,
      VersionCode: entry.versionCode || 0,
      ItemUniqueNo: entry.itemUniqueNo || 0,
      Explanation: entry.explanation || "",
      ExplanationReq: entry.explanationReq || "",
      UserToken: "",
    }));

    if (entries.length > 0) {
      const isEmpty = (value) =>
        value === null || value === undefined || value.toString().trim() === "";

      // STEP 1: Check for missing Total in the full dataset
      const firstMissingTotalEntry = data.find((entry) => isEmpty(entry.total));

      if (firstMissingTotalEntry) {
        const itemName = firstMissingTotalEntry.itemName;

        // Highlight only the entry with missing Total
        const updatedData = data.map((entry: any) => ({
          ...entry,
          showTotalRequired: entry.itemName === itemName,
          showExplanationRequired: false, // clear explanation highlights
        }));

        this.setState({ data: updatedData, isDataSaving: false });
        toast.error(`${itemName} - Total cannot be blank.`);
        return;
      }

      // STEP 2: If all Totals are valid, check for missing Explanation
      const firstMissingExplanationEntry = data.find(
        (entry) =>
          entry.explanationReq?.toString().toLowerCase() === "true" &&
          entry.total.toString() !== "0" &&
          isEmpty(entry.explanation)
      );

      if (firstMissingExplanationEntry) {
        const itemName = firstMissingExplanationEntry.itemName;

        // Highlight only the entry with missing Explanation
        data = data.map((entry) => ({
          ...entry,
          showExplanationRequired: entry.itemName === itemName,
          showTotalRequired: false, // clear total highlights
        }));

        this.setState({ data, isDataSaving: false });
        toast.error(`${itemName} - Explanation cannot be blank.`);
        return;
      }
    }

    const payload: IStatisticsPostRequest = {
      requests: entries,
    };

    try {
      const response: any = await DailyActivityService.SaveStatisticsDetails(
        payload
      );
      const { isSuccess, errorMessage, failedCount, failedEntries } = response;

      if (!isSuccess && errorMessage) {
        toast.error(errorMessage);
      } else if (failedCount > 0) {
        if (failedEntries[0]?.errorMessage) {
          toast.error(failedEntries[0]?.errorMessage);
        } else {
          toast.error(`${failedCount} entries failed to save.`);
        }
      } else {
        toast.success("Record Successfully Saved");
      }

      this.props.setCurrentTabStatusOnValuesChanges(false);
      this.setState({ isModified: false });
      this.getStatisticsData();
      this.props.refreshMainData();
    } catch (error) {
      toast.error("Failed to insert data.");
    } finally {
      this.setState({ isDataSaving: false });
    }
  };

  handleInputChange = (event, itemUniqueNo, field) => {
    
    const { data } = this.state;
    const rowIndex = data.findIndex(
      (x: any) => x.itemUniqueNo === itemUniqueNo
    );
    let value = event.target.value;
    if (field === "total") {
      value = value ? Utils.removeInvaildCharFromAmount(value, 10) : value;
    }

    if (
      field === "explanation" &&
      !/^[a-zA-Z0-9\s.,!?-]*$/.test(value.toString())
    ) {
      return;
    }

    this.setState((prevState) => {
      const newData = [...prevState.data];
      const updatedRow = { ...newData[rowIndex] };

      updatedRow[field] = value;
      updatedRow.isModified = true;

      // Clear red line if user starts typing a valid explanation
      if (field === "explanation" && value?.toString().trim()) {
        updatedRow.showExplanationRequired = false;
      }

      newData[rowIndex] = updatedRow;

      this.props.setCurrentTabStatusOnValuesChanges(true);
      return { data: newData, isModified: true };
    });
    if (
      this.scrollPos?.current?.firstChild?.className == "react-bootstrap-table" &&
      !this.state.isModified
    ) {
      setTimeout(() => {
        let offSetTop =
          this.scrollPos?.current?.childNodes[0]?.childNodes[0]?.childNodes[1]?.childNodes[rowIndex]?.offsetTop - 48;
        this.scrollPos?.current?.childNodes[0]?.scrollTo({
          top: offSetTop,
          behavior: "smooth",
        });
      }, 50);
    }
  };

  handleBlur = (event, row, rowIndex) => {
    this.setState((prevState) => {
      const newData = [...prevState.data];

      const arrivals =
        parseInt(
          newData.find((r) => r?.itemName?.toLowerCase() === "arrivals")?.total
        ) || 0;
      const roomOccupied =
        parseInt(
          newData.find((r) => r?.itemName?.toLowerCase() === "rooms occupied")
            ?.total
        ) || 0;
      const prevRoomsOccupied =
        parseInt(
          newData.find((r) => r?.itemName?.toLowerCase() === "rooms occupied")
            ?.prevTotal
        ) || 0;
      const compRoomsTotal =
        parseInt(
          newData.find((item) => item.itemName.toLowerCase() === "comp rooms")
            ?.total
        ) || 0;


      // Shared validation for autoCalculate === 1 or 3
      if ([1, 3].includes(row.autoCalculate) && arrivals > roomOccupied) {
        toast.error("ARRIVALS cannot be greater than Room Occupied.");

        const updatedData = newData.map((r) => {
          if (["1", "2", "3", "4"].includes(String(r.autoCalculate))) {
            return {
              ...r,
              total: "", // clear total if validation fails
            };
          }
          return r;
        });

        return { data: updatedData };
      }

      // Auto-calculate when autoCalculate === 1
      if (row.autoCalculate === 1) {
        const roomOccupiedRaw = newData.find(
          (r) => r?.itemName?.toLowerCase() === "rooms occupied"
        )?.total;

        // If room occupied is empty or not a number
        if (!roomOccupiedRaw || isNaN(roomOccupied)) {
          const updatedData = newData.map((r) => {
            if (r.autoCalculate === 4 || r.autoCalculate === 2) {
              return {
                ...r,
                total: "", // clear departures and stay overs
              };
            }
            if (r.autoCalculate === 6) {
              return {
                ...r,
                total: 0, // set rooms sold to 0
              };
            }
            return r;
          });

          return { data: updatedData };
        }

        // If room occupied has value, perform calculations
        const departures = prevRoomsOccupied + arrivals - roomOccupied;

        const updatedData = newData.map((r) => {
          if (r.autoCalculate === 4) {
            return {
              ...r,
              total: departures, // departures
            };
          }
          if (r.autoCalculate === 2) {
            return {
              ...r,
              total: prevRoomsOccupied - departures, // stay overs
            };
          }
          if (r.autoCalculate === 6) {
            return {
              ...r,
              total: roomOccupied - compRoomsTotal, // rooms sold
            };
          }
          return r;
        });

        return { data: updatedData };
      }

      // Auto-calculate when autoCalculate === 3
      if (row.autoCalculate === 3) {
        const departures = prevRoomsOccupied + arrivals - roomOccupied;

        const updatedData = newData.map((r) => {
          if (r.autoCalculate === 4) {
            return {
              ...r,
              total: departures, // departures value
            };
          }
          if (r.autoCalculate === 2) {
            return {
              ...r,
              total: prevRoomsOccupied - departures, // stay overs value
            };
          }
          return r;
        });

        return { data: updatedData };
      }

      return { data: newData };
    });
  };

  handleDiscard = () => {
    this.props.setCurrentTabStatusOnValuesChanges(false);
    this.setState({ isModified: false });
    this.getStatisticsData();
  };

  hoverOutEllipsis = () => {
    $(".tooltip").removeClass("show");
  };

  render() {
    const {
      searchInput,
      hideEmptyRows,
      isDataLoading,
      isModified,
      isDataSaving,
    } = this.state;

    return (
      <>
        {isDataLoading ? (
          <ReactPageLoader useas={"statistics"} />
        ) : (
          <div className="statistics-section reuse-sub-design">
            <div className="filter-section d-flex">
              <div className="search-input">
                <Form.Control
                  type="text"
                  onChange={(e) =>
                    this.setState({ searchInput: e.target.value })
                  }
                  placeholder="Search"
                  value={searchInput}
                />
              </div>

              <div className="ml-auto">
                <div className="togglebtn d-flex align-items-center">
                  <div className="label">Hide Empty Rows</div>
                  <Form.Check
                    id="showInactive"
                    key="showInactive"
                    type="switch"
                    label=""
                    checked={hideEmptyRows}
                    onChange={() =>
                      this.setState({ hideEmptyRows: !hideEmptyRows })
                    }
                  />
                </div>
              </div>
            </div>

            <div
              ref={this.scrollPos}
              className={`body-section ${isModified ? "footer-exist" : ""}`}
              onWheel={() => this.hoverOutEllipsis()}
            >
              <BootstrapTable
                keyField="itemUniqueNo"
                data={this.getFilteredData()}
                columns={this.getColumns()}
              />
            </div>
            {isModified && (
              <div className="footer-section d-flex align-items-center">
                <div className="msg">You have unsaved changes</div>
                <div className="ml-auto action">
                  <Button
                    variant="primary"
                    type="button"
                    disabled={isDataSaving}
                    onClick={() =>
                      confirmAlert({
                        title: "Unsaved Changes",
                        message:
                          "You have unsaved changes. Do you want to discard them?",
                        buttons: [
                          {
                            label: "Yes",
                            onClick: () => this.handleDiscard(),
                          },
                          { label: "No", onClick: () => { } },
                        ],
                      })
                    }
                    className="space btn btn-primary btn-discard"
                  >
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    type="button"
                    disabled={isDataSaving}
                    onClick={this.validateAndSave}
                    className="btn btn-primary"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }
}

export default Statistics;
